'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import { getAllQuestionnaireTemplatesInfo } from '@/_api/admin-questionnaires';
import { QuestionnaireTemplate } from '@/_types/questionnaireTemplates';
import { QuestionnaireTemplateProviderProps } from './QuestionnaireTemplateProvider.types';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';

const QuestionnaireTemplateContext =
  createContext<QuestionnaireTemplateProviderProps>({
    isLoading: false,
    loadQuestionnaireTemplates: () => {},
    questionnaireTemplates: [],
  });

const QuestionnaireTemplateProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [questionnaireTemplates, setQuestionnaireTemplates] = useState<
    Array<QuestionnaireTemplate>
  >([]);

  const { addToast } = useContext(ToastContext);
  const { isAdvisor, token } = useContext(UserContext);

  const loadQuestionnaireTemplates = useCallback(async () => {
    if (!token || !isAdvisor) return;

    setLoading(true);

    try {
      const templates = await getAllQuestionnaireTemplatesInfo({
        token,
      });
      const hasValidTemplates: boolean = Array.isArray(templates);

      if (hasValidTemplates) {
        setQuestionnaireTemplates(
          templates as unknown as Array<QuestionnaireTemplate>,
        );
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  }, [addToast, isAdvisor, token]);

  return (
    <QuestionnaireTemplateContext
      value={{
        isLoading,
        loadQuestionnaireTemplates,
        questionnaireTemplates,
      }}
    >
      {children}
    </QuestionnaireTemplateContext>
  );
};

export { QuestionnaireTemplateContext, QuestionnaireTemplateProvider };
